import React, { useState, useEffect, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { BottomNav } from './BottomNav';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';
import { syncPendingUploads } from '../../lib/syncManager';
import { toast } from '../ui/Toast';

export const Layout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isOnline = useOnlineStatus();
  const wasOfflineRef = useRef<boolean>(!isOnline);
  const queryClient = useQueryClient();

  // Auto-Sync: Wenn User von offline zu online wechselt
  useEffect(() => {
    if (isOnline && wasOfflineRef.current) {
      // Netzrueckkehr erkannt — ausstehende Uploads synchronisieren
      syncPendingUploads()
        .then(({ successCount }) => {
          if (successCount > 0) {
            toast.success(
              'Synchronisierung abgeschlossen',
              successCount === 1
                ? '1 Beleg wurde synchronisiert.'
                : `${successCount} Belege wurden synchronisiert.`
            );
            // Beleg-Liste neu laden
            queryClient.invalidateQueries({ queryKey: ['receipts'] });
          }
        })
        .catch(() => {
          // Sync-Fehler still behandeln — der Benutzer kann es spaeter erneut versuchen
        });
    }
    wasOfflineRef.current = !isOnline;
  }, [isOnline, queryClient]);

  return (
    <div className="flex h-screen overflow-hidden bg-gradient-to-br from-slate-50 via-white to-indigo-50/40 dark:from-[#0f0f1a] dark:via-[#0f0f1a] dark:to-[#131325]">
      {/* Sidebar (Desktop: always visible, Mobile: toggle) */}
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Header */}
        <Header onMenuToggle={() => setSidebarOpen(true)} />

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto pb-20 lg:pb-6">
          <div className="page-container">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Bottom Nav (Mobile) */}
      <BottomNav />
    </div>
  );
};
